import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CloseOutlined, MinusOutlined } from '@ant-design/icons';
import { Button, Flex, Typography } from 'antd';
import type { ResizeEdge } from './geometry';
import { applyDrag, applyResize } from './geometry';
import { useWindowGeometry } from './useWindowGeometry';
import { nextZIndex, peekZIndex } from './zIndexCounter';
import { useHotkeyScope, HotkeyHint, type HotkeyBinding } from '@/shared/lib/hotkeys';
import { useWindowHost } from '@/shared/lib/windows';

const RESIZE_EDGES: { edge: ResizeEdge; style: React.CSSProperties }[] = [
  { edge: 'n', style: { top: -3, left: 8, right: 8, height: 6, cursor: 'ns-resize' } },
  { edge: 's', style: { bottom: -3, left: 8, right: 8, height: 6, cursor: 'ns-resize' } },
  { edge: 'e', style: { right: -3, top: 8, bottom: 8, width: 6, cursor: 'ew-resize' } },
  { edge: 'w', style: { left: -3, top: 8, bottom: 8, width: 6, cursor: 'ew-resize' } },
  { edge: 'ne', style: { top: -3, right: -3, width: 10, height: 10, cursor: 'nesw-resize' } },
  { edge: 'nw', style: { top: -3, left: -3, width: 10, height: 10, cursor: 'nwse-resize' } },
  { edge: 'se', style: { bottom: -3, right: -3, width: 10, height: 10, cursor: 'nwse-resize' } },
  { edge: 'sw', style: { bottom: -3, left: -3, width: 10, height: 10, cursor: 'nesw-resize' } },
];

export interface FloatingWindowProps {
  /** Тип окна — по нему запоминаются размер/позиция (§1), не по конкретному документу. */
  kind: string;
  kindIndex?: number;
  title: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  /** Доп. горячие клавиши конкретного окна (например, сохранение) — Esc-закрытие уже встроено
   *  и добавлять его не нужно. */
  hotkeys?: HotkeyBinding[];
}

/**
 * Плавающее окно (§1: «модальные, свободно перемещаются и растягиваются по ширине и высоте,
 * размер и позиция запоминаются»). Без маски — можно открыть несколько окон одновременно
 * (продажа + возврат из неё, §11.1), страница под ними остаётся доступной.
 *
 * Escape закрывает окно (1С-стиль горячих клавиш, см. CLAUDE.md) — регистрируется здесь одним
 * местом на все окна документов/справочников. Несколько окон открыты одновременно без маски, и
 * порядок их z-index меняется кликом (`focus()` ниже) — приоритет горячих клавиш подтягивается
 * следом через `touch()`, иначе Esc всегда закрывал бы только последнее ОТКРЫТОЕ (а не активное)
 * окно.
 *
 * Сворачивание (как в Windows): окно остаётся смонтированным и лишь скрывается через
 * `visibility: hidden` — так не теряются введённые данные, прокрутка, позиция и размер, а
 * RTK Query-подписки продолжают обновлять содержимое. Развёрнутое окно выходит на передний
 * план. Пока окно свёрнуто, его горячие клавиши (Esc) отключены.
 */
export function FloatingWindow({ kind, kindIndex, title, onClose, footer, children, hotkeys }: FloatingWindowProps) {
  const { rect, setRect } = useWindowGeometry(kind, kindIndex);
  const [zIndex, setZIndex] = useState(() => nextZIndex());
  const host = useWindowHost();
  const minimized = host?.minimized ?? false;
  const setLabel = host?.setLabel;
  const label = typeof title === 'string' ? title : kind;

  // Развёрнутое окно — на передний план (подстройка состояния при смене пропса, не эффект).
  const [wasMinimized, setWasMinimized] = useState(minimized);
  if (wasMinimized !== minimized) {
    setWasMinimized(minimized);
    if (!minimized) setZIndex(nextZIndex());
  }

  useEffect(() => {
    setLabel?.(label);
  }, [setLabel, label]);
  const dragState = useRef<{ startX: number; startY: number; startRect: typeof rect } | null>(null);
  const resizeState = useRef<{ edge: ResizeEdge; startX: number; startY: number; startRect: typeof rect } | null>(null);

  const bindings = useMemo<HotkeyBinding[]>(
    () => [{ combo: 'Escape', description: 'закрыть окно', handler: onClose }, ...(hotkeys ?? [])],
    [onClose, hotkeys],
  );
  const { touch } = useHotkeyScope(bindings, !minimized);

  const focus = () => {
    touch();
    setZIndex((z) => (z === peekZIndex() ? z : nextZIndex()));
  };

  const onTitlePointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    focus();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragState.current = { startX: e.clientX, startY: e.clientY, startRect: rect };
  };

  const onTitlePointerMove = (e: React.PointerEvent) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setRect(applyDrag(dragState.current.startRect, dx, dy, { width: window.innerWidth, height: window.innerHeight }));
  };

  const endDrag = (e: React.PointerEvent) => {
    if (dragState.current) (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    dragState.current = null;
  };

  const onResizePointerDown = (edge: ResizeEdge) => (e: React.PointerEvent) => {
    focus();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    resizeState.current = { edge, startX: e.clientX, startY: e.clientY, startRect: rect };
  };

  const onResizePointerMove = (e: React.PointerEvent) => {
    if (!resizeState.current) return;
    const { edge, startX, startY, startRect } = resizeState.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    setRect(applyResize(startRect, edge, dx, dy, { width: window.innerWidth, height: window.innerHeight }));
  };

  const endResize = (e: React.PointerEvent) => {
    if (resizeState.current) (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    resizeState.current = null;
  };

  return (
    <div
      role="dialog"
      aria-hidden={minimized || undefined}
      onPointerDownCapture={focus}
      style={{
        position: 'fixed',
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        zIndex,
        background: '#fff',
        border: '1px solid #8c8c8c',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        visibility: minimized ? 'hidden' : 'visible',
        pointerEvents: minimized ? 'none' : undefined,
        opacity: minimized ? 0 : 1,
        transition: 'opacity 120ms ease, visibility 120ms',
      }}
    >
      {RESIZE_EDGES.map(({ edge, style }) => (
        <div
          key={edge}
          onPointerDown={onResizePointerDown(edge)}
          onPointerMove={onResizePointerMove}
          onPointerUp={endResize}
          style={{ position: 'absolute', ...style }}
        />
      ))}

      <Flex
        align="center"
        justify="space-between"
        onPointerDown={onTitlePointerDown}
        onPointerMove={onTitlePointerMove}
        onPointerUp={endDrag}
        style={{ padding: '4px 8px', background: '#f0f0f0', borderBottom: '1px solid #d9d9d9', cursor: 'move', flex: '0 0 auto' }}
      >
        <Typography.Text strong style={{ fontSize: 12 }}>
          {title}
        </Typography.Text>
        <Flex gap={2}>
          {host ? <Button type="text" size="small" icon={<MinusOutlined />} title="Свернуть" aria-label="Свернуть" onClick={host.minimize} /> : null}
          <Button type="text" size="small" icon={<CloseOutlined />} title="Закрыть" aria-label="Закрыть" onClick={onClose} />
        </Flex>
      </Flex>

      <div style={{ flex: '1 1 auto', overflow: 'auto', padding: 8 }}>{children}</div>

      {footer ? <div style={{ flex: '0 0 auto', padding: 8, borderTop: '1px solid #d9d9d9' }}>{footer}</div> : null}
      <div style={{ flex: '0 0 auto', padding: '0 8px 6px' }}>
        <HotkeyHint bindings={bindings} />
      </div>
    </div>
  );
}
